import { useEffect, useState } from "react";

export const COOKIE_CONSENT_KEY = "lpu_cookie_consent_v1";

export type ConsentChoice = "all" | "essential";

export interface ConsentData {
  choice: ConsentChoice;
  analytics: boolean;
  marketing: boolean;
  ts: string;
}

export function parseConsent(raw: string | null): ConsentData | null {
  if (!raw) return null;

  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === "object" && (parsed.choice === "all" || parsed.choice === "essential")) {
      const choice = parsed.choice;
      return {
        choice,
        analytics: choice === "all" || parsed.analytics === true,
        marketing: choice === "all" || parsed.marketing === true,
        ts: typeof parsed.ts === "string" ? parsed.ts : new Date().toISOString(),
      };
    }
  } catch {
    // starszy format: czysty string "all" lub "essential"
    if (raw === "all" || raw === "essential") {
      return {
        choice: raw,
        analytics: raw === "all",
        marketing: raw === "all",
        ts: new Date().toISOString(),
      };
    }
  }

  return null;
}

export function buildConsentChoice(analytics: boolean, marketing: boolean): ConsentChoice {
  return analytics || marketing ? "all" : "essential";
}

export function serializeConsent(data: ConsentData): string {
  return JSON.stringify(data);
}

export function useCookieConsent() {
  const [consent, setConsentState] = useState<ConsentData | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    const raw = localStorage.getItem(COOKIE_CONSENT_KEY);
    setConsentState(parseConsent(raw));
    setReady(true);
  }, []);

  const setConsent = (data: ConsentData) => {
    try {
      localStorage.setItem(COOKIE_CONSENT_KEY, serializeConsent(data));
    } catch {
      /* ignore */
    }
    setConsentState(data);
  };

  return {
    consent,
    ready,
    analyticsEnabled: consent?.analytics === true,
    marketingEnabled: consent?.marketing === true,
    setConsent,
  };
}
